/**
 * Site Entry View Component
 *
 * Displays a form for entering a siteId to load a site configuration.
 * Handles loading states, error display, and navigation on success.
 * Shows recent sites for quick access.
 *
 * Requirements:
 * - 1.1: Display prominent input field for entering a siteId
 * - 1.2: Display a "Next" button to proceed
 * - 1.3: Disable the "Next" button when siteId input is empty
 * - 1.4: Initiate loading of site configuration when user clicks "Next"
 * - 1.5: Display a loading indicator while site configuration is loading
 * - 6.1: Display "Site not found" error message for 404 status
 * - 6.2: Display generic error message for other error statuses
 * - 6.3: Allow user to modify siteId and retry when error is displayed
 */

import { useState, useCallback, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useSite } from '../context/useSite';
import { getRecentSites, addRecentSite, type RecentSite } from '../utils/recentSites';
import { useAnalytics } from '../../analytics';
import './SiteEntryView.css';

interface SiteEntryViewProps {
  readonly onSiteLoaded?: (siteId: string) => void;
}

/**
 * SiteEntryView component for entering a site ID and loading site configuration.
 *
 * @param onSiteLoaded - Optional callback invoked when a site is successfully loaded
 */
export function SiteEntryView({ onSiteLoaded }: SiteEntryViewProps): JSX.Element {
  const [siteId, setSiteId] = useState('');
  const [recentSites, setRecentSites] = useState<readonly RecentSite[]>([]);
  // Track the siteId that was explicitly requested by user action
  const [requestedSiteId, setRequestedSiteId] = useState<string | null>(null);
  const { site, isLoading, error, loadSite } = useSite();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();

  // Get search string to preserve query params in navigation
  const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';

  // Track page view on mount (Requirement 5.2)
  useEffect(() => {
    track('Pocket Admin Site Entry Viewed');
  }, [track]);

  // Load recent sites on mount
  useEffect(() => {
    setRecentSites(getRecentSites());
  }, []);

  // Navigate to site view only when the requested site is successfully loaded
  useEffect(() => {
    if (site && !isLoading && !error && requestedSiteId === site.siteId) {
      // Track site loaded successfully (Requirement 6.2)
      track('Pocket Admin Site Loaded', { SiteId: site.siteId });
      addRecentSite(site.siteId, site.name);
      setRecentSites(getRecentSites());
      onSiteLoaded?.(site.siteId);
      setRequestedSiteId(null);
      navigate(`/site/${site.siteId}${searchString}`);
    }
  }, [site, isLoading, error, navigate, onSiteLoaded, requestedSiteId, track]);

  // Track site not found error (Requirement 6.3)
  useEffect(() => {
    if (error?.type === 'not_found' && requestedSiteId) {
      track('Pocket Admin Site Not Found', { SiteId: requestedSiteId });
    }
  }, [error, requestedSiteId, track]);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSiteId(event.target.value.toLowerCase());
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedSiteId = siteId.trim();
      if (trimmedSiteId) {
        // Track site search submitted (Requirement 6.1)
        track('Pocket Admin Site Search Submitted', { SiteId: trimmedSiteId });
        setRequestedSiteId(trimmedSiteId);
        await loadSite(trimmedSiteId);
      }
    },
    [siteId, loadSite, track]
  );

  // Requirement 1.3: Disable Next button when input is empty or whitespace-only
  const isNextDisabled = siteId.trim() === '' || isLoading;

  return (
    <div className="site-entry">
      <div className="site-entry__container">
        <h1 className="site-entry__title">Enter Site ID</h1>
        <p className="site-entry__description">
          Enter the site ID to load the site configuration and access site-specific features.
        </p>

        <form className="site-entry__form" onSubmit={handleSubmit}>
          <div className="site-entry__input-group">
            {/* Requirement 1.1: Prominent input field for siteId */}
            <label htmlFor="siteId" className="site-entry__label">
              Site ID
            </label>
            <input
              id="siteId"
              type="text"
              className="site-entry__input"
              value={siteId}
              onChange={handleInputChange}
              placeholder="Enter site ID"
              disabled={isLoading}
              autoFocus
              aria-describedby={error ? 'site-entry-error' : undefined}
            />

            {/* Requirement 6.1: Display "Site not found" for 404 */}
            {error?.type === 'not_found' && (
              <span id="site-entry-error" className="site-entry__error" role="alert">
                Site not found
              </span>
            )}

            {/* Requirement 6.2: Display generic error for other errors */}
            {error?.type === 'api_error' && (
              <span id="site-entry-error" className="site-entry__error" role="alert">
                Failed to load site. Please try again.
              </span>
            )}
          </div>

          {/* Requirement 1.2: Next button, Requirement 1.3: Disabled when empty */}
          <button
            type="submit"
            className="site-entry__button"
            disabled={isNextDisabled}
          >
            {/* Requirement 1.5: Loading indicator while loading */}
            {isLoading ? (
              <>
                <span className="site-entry__spinner" aria-hidden="true" />
                Loading...
              </>
            ) : (
              'Next'
            )}
          </button>
        </form>

        {/* Recent Sites Section */}
        {recentSites.length > 0 && (
          <div className="site-entry__recent">
            <h2 className="site-entry__recent-title">Recent Sites</h2>
            <ul className="site-entry__recent-list">
              {recentSites.map((recentSite) => (
                <li key={recentSite.siteId} className="site-entry__recent-item">
                  <Link
                    to={`/site/${recentSite.siteId}${searchString}`}
                    className="site-entry__recent-link"
                    onClick={() => {
                      // Track recent site selection (Requirement 6.4)
                      track('Pocket Admin Recent Site Selected', { SiteId: recentSite.siteId });
                      setRequestedSiteId(recentSite.siteId);
                      loadSite(recentSite.siteId);
                    }}
                  >
                    <span className="site-entry__recent-name">{recentSite.name}</span>
                    <span className="site-entry__recent-id">{recentSite.siteId}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
